import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { Gamepad2, Twitter, Github, Youtube, Globe } from 'lucide-react';

export default function Footer() {
  const { t, language, setLanguage, LANGUAGES } = useI18n();
  const currentYear = new Date().getFullYear();
  const START_YEAR = 2026;
  const yearString = currentYear > START_YEAR ? `${START_YEAR}–${currentYear}` : `${START_YEAR}`;
  const copyrightBody = (t('copyright') || '© 2026 PlayMold. All rights reserved.').replace(/© ?\d{4}(?:[–\-]\d{4})?/, `© ${yearString}`);

  return (
    <footer className="bg-[#030305] border-t border-[#1E1E24] mt-auto">
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center">
                <Gamepad2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="font-black text-xl text-white">PlayMold</span>
              </div>
            </div>
            <p className="text-sm text-zinc-500 max-w-sm mb-4">
              Create stunning games with zero coding knowledge. Powered by cutting-edge AI technology.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a href="#" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                <Github className="w-4 h-4" />
              </a>
              <a href="#" className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Product</h4>
            <ul className="space-y-2">
              <li><Link to="/templates" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Templates</Link></li>
              <li><Link to="/gallery" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Community Gallery</Link></li>
              <li><Link to="/pricing" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Pricing</Link></li>
              <li><Link to="/marketplace" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Marketplace</Link></li>
              <li><Link to="/tutorial" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Tutorial</Link></li>
              <li><Link to="/faq" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-2">
              <li><Link to="/help" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Help Center</Link></li>
              <li><Link to="/contact" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Contact Us</Link></li>
              <li><Link to="/terms" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-sm text-zinc-300 hover:text-[#06B6D4] transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-[#1E1E24] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-zinc-300 font-medium" data-testid="footer-copyright">
            {copyrightBody}
          </p>
          
          {/* Language Selector */}
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-zinc-500" />
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-transparent text-sm text-zinc-400 border-none focus:outline-none cursor-pointer hover:text-white transition-colors"
            >
              {Object.entries(LANGUAGES).map(([code, { name, flag }]) => (
                <option key={code} value={code} className="bg-[#1E1E24]">
                  {flag} {name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 pt-6 border-t border-[#1E1E24]/50">
          <p className="text-xs text-zinc-400 leading-relaxed">
            <strong className="text-zinc-200">Disclaimer:</strong> PlayMold is an AI-powered game creation platform. 
            Games created using this platform are the intellectual property of their creators. 
            We are not responsible for content created by users. AI-generated assets may have usage limitations. 
            By using this service, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </footer>
  );
}
